/**
 * Every call the shell can make, one function per live route.
 *
 * Keeping the URL templates in one file is what makes "does this screen have a
 * backend?" answerable by reading a single module — the deliverable rule was
 * that a screen wired to nothing is worse than no screen.
 */
import { request } from './http';
import type {
  ApprovalSummary,
  BrandView,
  ConnectorListView,
  ConnectorView,
  CreatePropertyRequest,
  ExtractionAcceptedView,
  FactEvidenceListView,
  ImportView,
  PropertyListView,
  PropertyView,
  ContentVersionView,
  FactState,
  FactView,
  MetricSnapshotListView,
  Page,
  PauseResponse,
} from './types';

const base = (workspaceId: string) => `/api/v1/workspaces/${encodeURIComponent(workspaceId)}`;

export function listBrands(workspaceId: string, signal?: AbortSignal): Promise<Page<BrandView>> {
  return request(`${base(workspaceId)}/brands`, signal ? { signal } : {});
}

export function getBrand(
  workspaceId: string,
  brandId: string,
  signal?: AbortSignal,
): Promise<BrandView> {
  return request(
    `${base(workspaceId)}/brands/${encodeURIComponent(brandId)}`,
    signal ? { signal } : {},
  );
}

export function listFacts(
  workspaceId: string,
  brandId: string,
  state: FactState | 'all',
  signal?: AbortSignal,
): Promise<Page<FactView>> {
  return request(`${base(workspaceId)}/brands/${encodeURIComponent(brandId)}/facts`, {
    ...(state === 'all' ? {} : { query: { state } }),
    ...(signal ? { signal } : {}),
  });
}

/**
 * The quotations a fact rests on (design doc §8.4).
 *
 * Fetched only when a reviewer opens the drawer: evidence is read at the moment
 * of a verify decision, not on every list render.
 */
export function listFactEvidence(
  workspaceId: string,
  factId: string,
  signal?: AbortSignal,
): Promise<FactEvidenceListView> {
  return request(
    `${base(workspaceId)}/facts/${encodeURIComponent(factId)}/evidence`,
    signal ? { signal } : {},
  );
}

export function verifyFact(
  workspaceId: string,
  factId: string,
  idempotencyKey: string,
): Promise<FactView> {
  return request(`${base(workspaceId)}/facts/${encodeURIComponent(factId)}/verify`, {
    method: 'POST',
    idempotencyKey,
  });
}

export function revokeFact(
  workspaceId: string,
  factId: string,
  reason: string,
  idempotencyKey: string,
): Promise<FactView> {
  return request(`${base(workspaceId)}/facts/${encodeURIComponent(factId)}/revoke`, {
    method: 'POST',
    body: { reason },
    idempotencyKey,
  });
}

export function listProperties(
  workspaceId: string,
  brandId: string,
  signal?: AbortSignal,
): Promise<PropertyListView> {
  return request(
    `${base(workspaceId)}/brands/${encodeURIComponent(brandId)}/properties`,
    signal ? { signal } : {},
  );
}

/**
 * Adds a property to a brand (design doc §6.1 step 1).
 *
 * The server parses `canonical_uri` into the `CanonicalUrl` value object and
 * refuses anything that is not `https` (outside loopback). It is created
 * `unverified`: crawling a public page needs no ownership proof, and reading an
 * authenticated source needs more than this call can give (§16.1).
 *
 * Returns the property in the list's own shape, so the caller can place it
 * straight into the list it already holds.
 */
export function createProperty(
  workspaceId: string,
  brandId: string,
  input: CreatePropertyRequest,
  idempotencyKey: string,
): Promise<PropertyView> {
  return request(`${base(workspaceId)}/brands/${encodeURIComponent(brandId)}/properties`, {
    method: 'POST',
    body: input,
    idempotencyKey,
  });
}

/**
 * Starts a crawl (design doc §16.1).
 *
 * Returns `202` with an import to watch. Nothing has been read when this
 * resolves, and — as §16.2 insists — an import creates no facts even when it
 * finishes.
 */
export function startImport(
  workspaceId: string,
  propertyId: string,
  idempotencyKey: string,
): Promise<ImportView> {
  return request(`${base(workspaceId)}/properties/${encodeURIComponent(propertyId)}/imports`, {
    method: 'POST',
    idempotencyKey,
  });
}

/**
 * Queues one bounded run per imported page to propose candidate facts.
 *
 * Every candidate is proposed `unverified`; only a person can verify one
 * (design doc §8.3).
 */
export function extractFacts(
  workspaceId: string,
  importId: string,
  idempotencyKey: string,
): Promise<ExtractionAcceptedView> {
  return request(`${base(workspaceId)}/imports/${encodeURIComponent(importId)}/extract-facts`, {
    method: 'POST',
    idempotencyKey,
  });
}

/**
 * Stored metric rows for a brand, inside an explicit window (design doc §18).
 *
 * The window is always sent rather than left to the server's default, because
 * the screen shows which period it is reporting on and the two must be the same
 * period. The response echoes the window the server actually resolved, and that
 * echo is what the screen renders.
 */
export function listBrandMetrics(
  workspaceId: string,
  brandId: string,
  window: { readonly from: string; readonly to: string },
  signal?: AbortSignal,
): Promise<MetricSnapshotListView> {
  return request(`${base(workspaceId)}/brands/${encodeURIComponent(brandId)}/metrics`, {
    query: { from: window.from, to: window.to },
    ...(signal ? { signal } : {}),
  });
}

/**
 * Files a connector's metrics under a brand (design doc §18).
 *
 * `brandId` of `null` clears the binding, which stops the scheduler syncing the
 * connector — it refuses rather than guessing which brand the numbers describe.
 */
export function bindConnectorMetrics(
  workspaceId: string,
  connectorId: string,
  brandId: string | null,
  propertyId: string | null,
): Promise<ConnectorView> {
  return request(
    `${base(workspaceId)}/connectors/${encodeURIComponent(connectorId)}/metrics-binding`,
    { method: 'PUT', body: { brand_id: brandId, property_id: propertyId } },
  );
}

export function listConnectors(
  workspaceId: string,
  signal?: AbortSignal,
): Promise<ConnectorListView> {
  return request(`${base(workspaceId)}/connectors`, signal ? { signal } : {});
}

/**
 * The per-connector kill switch (design doc §10.5).
 *
 * Returns the connector as it now stands rather than a bare acknowledgement,
 * because resuming does **not** necessarily make a connector usable: one whose
 * credential the provider rejected comes back still blocked, and the screen has
 * to be able to say so.
 */
export function setConnectorPaused(
  workspaceId: string,
  connectorId: string,
  paused: boolean,
  idempotencyKey: string,
): Promise<ConnectorView> {
  return request(`${base(workspaceId)}/connectors/${encodeURIComponent(connectorId)}/pause`, {
    method: 'POST',
    body: { paused },
    idempotencyKey,
  });
}

/**
 * Starts an authorization at the provider (design doc §15.5).
 *
 * Returns a URL to send the browser to. Nothing has changed when this resolves:
 * the connector stays exactly as it was until the flow completes, so abandoning
 * the consent screen cannot leave a dead connector looking healthy.
 */
export function authorizeConnector(
  workspaceId: string,
  connectorId: string,
  requestedCapabilities: readonly string[],
  redirectUri: string,
): Promise<{ authorization_url: string; expires_at: string; note: string }> {
  return request(`${base(workspaceId)}/connectors/${encodeURIComponent(connectorId)}/authorize`, {
    method: 'POST',
    body: { requested_capabilities: requestedCapabilities, redirect_uri: redirectUri },
  });
}

export function listPendingApprovals(
  workspaceId: string,
  signal?: AbortSignal,
): Promise<Page<ApprovalSummary>> {
  return request(`${base(workspaceId)}/approval-requests`, signal ? { signal } : {});
}

export function getContentVersion(
  workspaceId: string,
  versionId: string,
  signal?: AbortSignal,
): Promise<ContentVersionView> {
  return request(
    `${base(workspaceId)}/content/${encodeURIComponent(versionId)}`,
    signal ? { signal } : {},
  );
}

export function listVersionHistory(
  workspaceId: string,
  contentItemId: string,
  signal?: AbortSignal,
): Promise<Page<ContentVersionView>> {
  return request(
    `${base(workspaceId)}/content-items/${encodeURIComponent(contentItemId)}/versions`,
    signal ? { signal } : {},
  );
}

export function pauseWorkspace(
  workspaceId: string,
  idempotencyKey: string,
): Promise<PauseResponse> {
  return request(`${base(workspaceId)}/pause`, { method: 'POST', idempotencyKey });
}

export function resumeWorkspace(
  workspaceId: string,
  idempotencyKey: string,
): Promise<PauseResponse> {
  return request(`${base(workspaceId)}/resume`, { method: 'POST', idempotencyKey });
}

// --- Workspace, portfolio, brand workspace tabs, settings (web-workspace) -----
//
// Every type below is the generated contract type (`components['schemas']`):
// these routes are described by `contracts/openapi.yaml` exactly as served.

import type { components as WsSchemas } from '@cedarreachai/api-client';

type WS = WsSchemas['schemas'];

/** The signed-in user and every workspace they belong to. Pre-tenant. */
export function getSession(signal?: AbortSignal): Promise<WS['Session']> {
  return request('/api/v1/me', signal ? { signal } : {});
}

export function listWorkspaces(signal?: AbortSignal): Promise<WS['WorkspacePage']> {
  return request('/api/v1/workspaces', signal ? { signal } : {});
}

/** Creates a workspace. It starts at automation level `draft`; nothing self-promotes. */
export function createWorkspace(
  input: WS['CreateWorkspaceRequest'],
  idempotencyKey: string,
): Promise<WS['Workspace']> {
  return request('/api/v1/workspaces', { method: 'POST', body: input, idempotencyKey });
}

export function getWorkspace(workspaceId: string, signal?: AbortSignal): Promise<WS['Workspace']> {
  return request(base(workspaceId), signal ? { signal } : {});
}

/** Changes the automation ceiling. `If-Match` carries the workspace `version`. */
export function updateWorkspaceAutomation(
  workspaceId: string,
  automationLevel: WS['AutomationLevel'],
  version: number,
): Promise<WS['Workspace']> {
  return request(base(workspaceId), {
    method: 'PATCH',
    body: { automation_level: automationLevel },
    ifMatchVersion: version,
  });
}

/** The five §5.2 questions, answered per brand by the server. */
export function getPortfolio(
  workspaceId: string,
  signal?: AbortSignal,
): Promise<WS['PortfolioSummary']> {
  return request(`${base(workspaceId)}/portfolio`, signal ? { signal } : {});
}

export function pauseBrand(
  workspaceId: string,
  brandId: string,
  idempotencyKey: string,
): Promise<WS['BrandPauseResponse']> {
  return request(`${base(workspaceId)}/brands/${encodeURIComponent(brandId)}/pause`, {
    method: 'POST',
    idempotencyKey,
  });
}

export function resumeBrand(
  workspaceId: string,
  brandId: string,
  idempotencyKey: string,
): Promise<WS['BrandPauseResponse']> {
  return request(`${base(workspaceId)}/brands/${encodeURIComponent(brandId)}/resume`, {
    method: 'POST',
    idempotencyKey,
  });
}

/** `createBrand`: a new brand always starts at automation level `draft`. */
export function createBrand(
  workspaceId: string,
  input: WS['CreateBrandRequest'],
): Promise<WS['WorkspaceBrand']> {
  return request(`${base(workspaceId)}/brands`, { method: 'POST', body: input });
}

/** `updateWorkspaceBrand`: name, description, default locale, risk profile; `If-Match`. */
export function updateBrand(
  workspaceId: string,
  brandId: string,
  input: WS['UpdateWorkspaceBrandRequest'],
  version: number,
): Promise<WS['WorkspaceBrand']> {
  return request(`${base(workspaceId)}/brands/${encodeURIComponent(brandId)}`, {
    method: 'PATCH',
    body: input,
    ifMatchVersion: version,
  });
}

/** `setBrandAutomationLevel`: the brand's own ceiling; `If-Match`. */
export function setBrandAutomationLevel(
  workspaceId: string,
  brandId: string,
  automationLevel: WS['AutomationLevel'],
  version: number,
): Promise<WS['WorkspaceBrand']> {
  return request(`${base(workspaceId)}/brands/${encodeURIComponent(brandId)}/automation-level`, {
    method: 'PUT',
    body: { automation_level: automationLevel },
    ifMatchVersion: version,
  });
}

export function listAudienceSegments(
  workspaceId: string,
  brandId: string,
  signal?: AbortSignal,
): Promise<{ items: WS['AudienceSegmentSummary'][]; next_cursor: string | null }> {
  return request(
    `${base(workspaceId)}/brands/${encodeURIComponent(brandId)}/audience-segments`,
    signal ? { signal } : {},
  );
}

/** `addressed_to` may be omitted: the server applies the brand's §10.4 default. */
export function createAudienceSegment(
  workspaceId: string,
  brandId: string,
  input: WS['CreateWorkspaceAudienceSegmentRequest'],
  idempotencyKey: string,
): Promise<WS['AudienceSegmentSummary']> {
  return request(`${base(workspaceId)}/brands/${encodeURIComponent(brandId)}/audience-segments`, {
    method: 'POST',
    body: input,
    idempotencyKey,
  });
}

export function listBrandBookVersions(
  workspaceId: string,
  brandId: string,
  signal?: AbortSignal,
): Promise<{ items: WS['BrandBookVersionSummary'][]; next_cursor: string | null }> {
  return request(
    `${base(workspaceId)}/brands/${encodeURIComponent(brandId)}/brand-book/versions`,
    signal ? { signal } : {},
  );
}

/** Appends a new `draft` version. Versions are never edited in place. */
export function createBrandBookVersion(
  workspaceId: string,
  brandId: string,
  rules: WS['BrandBookRules'],
  idempotencyKey: string,
): Promise<WS['BrandBookVersionSummary']> {
  return request(`${base(workspaceId)}/brands/${encodeURIComponent(brandId)}/brand-book/versions`, {
    method: 'POST',
    body: { structured_rules: rules },
    idempotencyKey,
  });
}

export function approveBrandBookVersion(
  workspaceId: string,
  versionId: string,
  idempotencyKey: string,
): Promise<WS['BrandBookVersionSummary']> {
  return request(
    `${base(workspaceId)}/brand-book-versions/${encodeURIComponent(versionId)}/approve`,
    { method: 'POST', idempotencyKey },
  );
}

/** Replaces the property's extra collector origins (ADR-013). The server normalises and validates. */
export function setEventOrigins(
  workspaceId: string,
  propertyId: string,
  origins: readonly string[],
): Promise<WS['PropertyEventOrigins']> {
  return request(
    `${base(workspaceId)}/properties/${encodeURIComponent(propertyId)}/event-origins`,
    { method: 'PUT', body: { origins } },
  );
}

export function listConversionGoals(
  workspaceId: string,
  propertyId: string,
  signal?: AbortSignal,
): Promise<WS['WorkspaceConversionGoalList']> {
  return request(
    `${base(workspaceId)}/properties/${encodeURIComponent(propertyId)}/conversion-goals`,
    signal ? { signal } : {},
  );
}

/** `is_primary: true` replaces the property's current primary goal. */
export function createConversionGoal(
  workspaceId: string,
  propertyId: string,
  input: WS['CreateWorkspaceConversionGoalRequest'],
  idempotencyKey: string,
): Promise<WS['WorkspaceConversionGoal']> {
  return request(
    `${base(workspaceId)}/properties/${encodeURIComponent(propertyId)}/conversion-goals`,
    { method: 'POST', body: input, idempotencyKey },
  );
}

/** Issues a new public event key. The previous key stops working immediately. */
export function rotateEventKey(
  workspaceId: string,
  propertyId: string,
  idempotencyKey: string,
): Promise<WS['PropertyEventKey']> {
  return request(
    `${base(workspaceId)}/properties/${encodeURIComponent(propertyId)}/event-key:rotate`,
    { method: 'POST', idempotencyKey },
  );
}

/** A manually entered fact. The server always stores it `unverified`. */
export function createFact(
  workspaceId: string,
  brandId: string,
  input: WS['CreateWorkspaceFactRequest'],
  idempotencyKey: string,
): Promise<WS['CreatedFact']> {
  return request(`${base(workspaceId)}/brands/${encodeURIComponent(brandId)}/facts`, {
    method: 'POST',
    body: input,
    idempotencyKey,
  });
}

/** Merge-patch. Changing `statement` on a verified fact returns it to `unverified`. */
export function updateFact(
  workspaceId: string,
  factId: string,
  patch: WS['UpdateWorkspaceFactRequest'],
  version: number,
): Promise<WS['FactSummary']> {
  return request(`${base(workspaceId)}/facts/${encodeURIComponent(factId)}`, {
    method: 'PATCH',
    body: patch,
    ifMatchVersion: version,
  });
}

export interface AuditEventFilters {
  readonly actor?: string;
  readonly action?: string;
  readonly target_type?: string;
  readonly target_id?: string;
  readonly campaign_id?: string;
  readonly run_id?: string;
  readonly from?: string;
  readonly to?: string;
}

export function listAuditEvents(
  workspaceId: string,
  filters: AuditEventFilters,
  cursor: string | null,
  signal?: AbortSignal,
): Promise<WS['AuditEventPage']> {
  return request(`${base(workspaceId)}/audit-events`, {
    query: { ...filters, cursor: cursor ?? undefined, limit: '50' },
    ...(signal ? { signal } : {}),
  });
}

export type CostGroupBy = 'provider' | 'model' | 'task' | 'run';

export function getCostLedger(
  workspaceId: string,
  filters: {
    readonly group_by: CostGroupBy;
    readonly brand_id?: string;
    readonly from?: string;
    readonly to?: string;
  },
  signal?: AbortSignal,
): Promise<WS['CostLedgerReport']> {
  return request(`${base(workspaceId)}/cost-ledger`, {
    query: { ...filters },
    ...(signal ? { signal } : {}),
  });
}

export interface AgentRunFilters {
  readonly state?: WS['AgentRunState'];
  readonly brand_id?: string;
  readonly campaign_id?: string;
}

export function listAgentRuns(
  workspaceId: string,
  filters: AgentRunFilters,
  signal?: AbortSignal,
): Promise<WS['AgentRunList']> {
  return request(`${base(workspaceId)}/agent-runs`, {
    query: { ...filters },
    ...(signal ? { signal } : {}),
  });
}

export function getAgentRun(
  workspaceId: string,
  runId: string,
  signal?: AbortSignal,
): Promise<WS['AgentRunDetail']> {
  return request(
    `${base(workspaceId)}/agent-runs/${encodeURIComponent(runId)}`,
    signal ? { signal } : {},
  );
}

/** Cooperative: a running run stops at the next step boundary and keeps its partial work. */
export function cancelAgentRun(workspaceId: string, runId: string): Promise<unknown> {
  return request(`${base(workspaceId)}/agent-runs/${encodeURIComponent(runId)}/cancel`, {
    method: 'POST',
  });
}
